import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ApiService from '../../services/api';
import {
  clearPartnerPortalSession,
  persistPartnerPortalSession,
} from '../../utils/partnerPortalSession';
import { clearClientAuthSession } from '../../utils/clearClientAuthSession';
import { hasClientAuthSession, getClientAccessToken } from '../../utils/sessionAuth';

interface PartnerRouteProps {
  children: React.ReactNode;
}

const PartnerRoute: React.FC<PartnerRouteProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [isPartner, setIsPartner] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!hasClientAuthSession()) {
        setIsPartner(false);
        setChecking(false);
        return;
      }

      try {
        const response = await ApiService.request('/partner-account/me');
        const user = response?.data?.user;
        const partner = response?.data?.partner;
        if (user?.role === 'partner' && partner) {
          const token = getClientAccessToken();
          persistPartnerPortalSession(user, partner, token);
          setIsPartner(true);
        } else {
          // Wrong role for this portal — deny access without destroying the MSME session.
          clearPartnerPortalSession();
          setIsPartner(false);
        }
      } catch (error: unknown) {
        clearPartnerPortalSession();
        const status = (error as { response?: { status?: number }; status?: number })?.response?.status
          ?? (error as { status?: number })?.status;
        // Only clear the shared client session when the token itself is invalid/expired.
        // A 403 (authenticated but not a partner) must not log the MSME user out (DEEP-BUG-012).
        if (status === 401 || !hasClientAuthSession()) {
          clearClientAuthSession();
        }
        setIsPartner(false);
      } finally {
        setChecking(false);
      }
    };

    validate();
  }, []);

  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isPartner) {
    // Keep MSME users in their workspace; only send truly unauthenticated users to login.
    return <Navigate to={hasClientAuthSession() ? '/dashboard' : '/login'} replace />;
  }

  return <>{children}</>;
};

export default PartnerRoute;
